import { Center, Flex, Tag, Icon, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { Tooltip } from "../../common/components/Tooltip";
import { FiLayers } from "react-icons/fi";
import { EventStatusTag } from "../../common/sourcing-events/EventStatusTag";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  FrameworkAgreementDto,
  MiniCompetitionSummaryDto,
  PublicEventStatusDto,
  useListMiniCompetitionsQuery,
} from "../../autogen/bff-api";
import { EntityViewSkeleton } from "../../common/EntityViewSkeleton";
import { useApiError } from "../../common/errors/useApiError";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { urls } from "../../urls";
import { remainingUnderCeiling } from "./callOffAvailability";
import { NewMiniCompetitionModal } from "./NewMiniCompetitionModal";
import { PanelActionButton } from "./PanelActionButton";
import { HorizontalScrollBox } from "../../common/HorizontalScrollBox";
import { RowTitle } from "./RowTitle";
import { SectionCard } from "./SectionCard";
import { StartRefusalNote } from "./StartRefusalNote";

const NOT_YET_AWARDED: PublicEventStatusDto[] = [
  PublicEventStatusDto.Draft,
  PublicEventStatusDto.PublicationPending,
  PublicEventStatusDto.Published,
  PublicEventStatusDto.PublicationFailed,
  PublicEventStatusDto.Evaluation,
  PublicEventStatusDto.Evaluated,
];

const exceedsCeiling = (competition: MiniCompetitionSummaryDto, remaining: number | null): boolean =>
  remaining !== null &&
  NOT_YET_AWARDED.includes(competition.status) &&
  (remaining <= 0 || (competition.callOffValue != null && Number(competition.callOffValue) > remaining));

const awaitsEvaluation = (competition: MiniCompetitionSummaryDto): boolean =>
  competition.status === PublicEventStatusDto.Published &&
  competition.bidSubmissionDeadline != null &&
  new Date(competition.bidSubmissionDeadline).getTime() < Date.now();

export const MiniCompetitionsSection = ({
  framework,
  canEdit,
}: {
  framework: FrameworkAgreementDto;
  canEdit: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const displayer = useApiError();
  const [isStarting, setIsStarting] = useState(false);
  const { data, error, isLoading } = useListMiniCompetitionsQuery({ frameworkAgreementId: framework.id });

  const offersMiniCompetition = canEdit && framework.allowsMiniCompetitions && framework.mayOpenMiniCompetition === true;

  if (error) return displayer.show(error);
  if (isLoading || !data) return <EntityViewSkeleton />;

  const miniCompetitions = data.miniCompetitions ?? [];
  const remaining = remainingUnderCeiling(framework);

  return (
    <Stack spacing="3">
      <SectionCard
        title={t("Mini-competitions")}
        notice={offersMiniCompetition && <StartRefusalNote framework={framework} startsMiniCompetition mt="2" />}
        action={
          offersMiniCompetition && (
            <PanelActionButton
              framework={framework}
              label={t("New mini-competition")}
              startsMiniCompetition
              onClick={() => setIsStarting(true)}
            />
          )
        }
      >
        {miniCompetitions.length === 0 ? (
          <Center py="16" px="6" flexDirection="column" gap="3" color="smMuted">
            <Icon as={FiLayers} boxSize="28px" />
            <Text fontWeight="medium" color="smText">
              {t("No mini-competitions have been run on this framework agreement yet")}
            </Text>
            <Text fontSize="sm" maxW="420px" textAlign="center">
              {t("A mini-competition asks the suppliers on the panel to bid for one call-off.")}
            </Text>
            {offersMiniCompetition && (
              <PanelActionButton
                framework={framework}
                label={t("New mini-competition")}
                startsMiniCompetition
                onClick={() => setIsStarting(true)}
              />
            )}
          </Center>
        ) : (
          <HorizontalScrollBox>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Mini-competition")}</Th>
                  <Th>{t("Status")}</Th>
                  <Th>{t("Bid deadline")}</Th>
                  <Th>{t("Invited")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {miniCompetitions.map((competition) => (
                  <Tr
                    key={competition.eventId}
                    cursor={competition.mayOpen ? "pointer" : undefined}
                    _hover={competition.mayOpen ? { bg: "smBackgroundSecondary" } : undefined}
                    onClick={
                      competition.mayOpen
                        ? () => navigate(urls.app.sourcingEvents.view.go(competition.eventId))
                        : undefined
                    }
                  >
                    <Td>
                      <Flex align="center" gap="2" flexWrap="wrap">
                        <RowTitle>{competition.title}</RowTitle>
                        {exceedsCeiling(competition, remaining) && (
                          <Tooltip label={t("Awarding it would take the framework agreement past its ceiling value.")}>
                            <Tag size="sm" colorScheme="orange">
                              {t("Over the ceiling value")}
                            </Tag>
                          </Tooltip>
                        )}
                      </Flex>
                    </Td>
                    <Td>
                      <EventStatusTag event={{ kind: "PUBLIC", status: competition.status }} size="sm" />
                    </Td>
                    <Td>
                      <Flex align="center" gap="2" flexWrap="wrap">
                        <Text as="span">
                          {competition.bidSubmissionDeadline ? displayDate(competition.bidSubmissionDeadline) : "-"}
                        </Text>
                        {awaitsEvaluation(competition) && (
                          <Tag size="sm" colorScheme="orange">
                            {t("Past the deadline")}
                          </Tag>
                        )}
                      </Flex>
                    </Td>
                    <Td>{competition.invitedSupplierCount}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </HorizontalScrollBox>
        )}
      </SectionCard>
      {isStarting && (
        <NewMiniCompetitionModal
          framework={framework}
          isOpen={isStarting}
          onClose={() => setIsStarting(false)}
        />
      )}
    </Stack>
  );
};
